import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { Button, message } from "../../ui/ot";
import { useCRUDController } from "../../ui/classify_structure/CRUDController";

const ScrappedListExcelExport = ({ data, propsLoading = null, mode }) => {
    const [loading, setLoading] = useState(false)

    /* --------- 原本的前處理完全保留 --------- */
    const translateToExportData = (record) => {
        const list_task_data = record?.list_task_data || [];
        const list_task_data_first = list_task_data?.find(() => true);
        const items_list_items_data = (record?.items_list_items_data || []).map(
            (v) => {
                const tags = [
                    "item_damaged_authorised_values",
                    "item_notforloan_authorised_values",
                    "item_restricted_authorised_values",
                    "item_withdrawn_authorised_values",
                ];
                let statusCount = 0;
                tags.forEach((t) => v?.[t] && statusCount++);
                const status = v?.issue_cancheckout
                    ? "可借閱"
                    : statusCount
                        ? "不可借出 :" +
                        (v.item_damaged_authorised_values || "") +
                        (v.item_notforloan_authorised_values || "") +
                        (v.item_restricted_authorised_values || "") +
                        (v.item_withdrawn_authorised_values || "")
                        : "不可借出";

                return {
                    ...v,
                    title: (v.bib_title || "") + (v.bib_subtitle || ""),
                    status,
                };
            }
        );
        return { ...record, task_name: list_task_data_first?.task_name, items_list_items_data };
    };
    /* -------------------------------------- */

    const handleExport = async (data, setLoading) => {
        try {
            let d = translateToExportData(data);
            /* ========== 1. 準備資料 ========== */
            let infoLeft = [
                ["報廢單編號", d?.list_number || ""],
                ["建立人", d?.create_user_name || ""],
                ["報廢狀態", d?.item_withdraw_name || ""],
                ["報廢原因", d?.reason || ""],
            ];
            const infoRight = [
                ["報廢單名稱", d?.task_name || ""],
                ["建立時間", d?.create_time || ""],
            ];
            const header = [
                "項次",
                "館藏條碼",
                "書名",
                "作者",
                "出版資訊",
                "館藏類型",
                "所屬分館",
                "索書號",
                "狀態",
                "複本號",
            ];
            const rows = (d?.items_list_items_data || []).map((it, idx) => [
                idx + 1,
                it?.item_barcode || "",
                it?.title || "",
                it?.bib_author || "",
                it?.bib_publisher || "",
                it?.item_itype_description || "",
                it?.item_branchname_home || "",
                it?.item_itemcallnumber || "",
                it?.status || "",
                it?.item_copynumber || "",
            ]);

            /* ========== 2. 建立 AOArray 版式 ========== */
            const sheet = [];

            // 標題列先推入 10 個空欄占位
            sheet.push(["館藏報廢單資訊", "", "", "", "", "", "", "", "", ""]);
            // 左右 info
            infoLeft.forEach((row, idx) => {
                sheet.push([]);
                sheet[idx + 1][0] = row[0];
                sheet[idx + 1][1] = row[1];
                sheet[idx + 1][2] = "";
                sheet[idx + 1][3] = "";
                sheet[idx + 1][4] = "";
                // 右側僅前兩列
                if (idx < infoRight.length) {
                    sheet[idx + 1][5] = infoRight[idx][0];
                    sheet[idx + 1][6] = infoRight[idx][1];
                    sheet[idx + 1][7] = "";
                    sheet[idx + 1][8] = "";
                    sheet[idx + 1][9] = "";
                }
            });
            // 空分隔列
            sheet.push(new Array(10).fill(""));
            // 表頭標題列
            sheet.push(["報廢館藏列表", "", "", "", "", "", "", "", "", ""]);

            // 表頭
            sheet.push(header);
            // 明細
            rows.forEach((r) => sheet.push(r));
            // 總計列
            const totalRowIdx = sheet.length;
            sheet.push([
                "總計",
                "",
                "",
                "",
                "",
                "",
                rows.length,
                "",
                "筆館藏",
                "",
            ]);

            /* ========== 3. 產生工作表 ========== */
            const ws = XLSX.utils.aoa_to_sheet(sheet);

            /* ========== 4. 樣式 ========== */
            const defaultBorder = {
                top: { style: "thin", color: { rgb: "000000" } },
                bottom: { style: "thin", color: { rgb: "000000" } },
                left: { style: "thin", color: { rgb: "000000" } },
                right: { style: "thin", color: { rgb: "000000" } },
            };
            const setStyle = (ref, style) => {
                if (!ws[ref]) ws[ref] = { t: "s", v: "" };
                ws[ref].s = { ...ws[ref].s, ...style };
            };
            // 外框粗藍線 (A1:J{totalRowIdx+1})
            const outerStyle = { border: { ...defaultBorder } };
            Object.values(outerStyle.border).forEach((b) => {
                b.style = "thin";
                b.color = { rgb: "000000" };
            });
            for (let c = 0; c < 10; c++) {
                const topRef = XLSX.utils.encode_cell({ r: 0, c });
                const bottomRef = XLSX.utils.encode_cell({ r: totalRowIdx, c });
                setStyle(topRef, { border: { top: outerStyle.border.top } });
                setStyle(bottomRef, { border: { bottom: outerStyle.border.bottom } });
            }
            for (let r = 0; r <= totalRowIdx; r++) {
                const leftRef = XLSX.utils.encode_cell({ r, c: 0 });
                const rightRef = XLSX.utils.encode_cell({ r, c: 9 });
                setStyle(leftRef, { border: { left: outerStyle.border.left } });
                setStyle(rightRef, { border: { right: outerStyle.border.right } });
            }

            // 標題列
            setStyle("A1", {
                font: { bold: true, sz: 20 },
                alignment: { horizontal: "center", vertical: "center" },
                fill: { fgColor: { rgb: "D9E1F2" } },
            });

            // info 區與表頭列樣式
            const lightFill = { fill: { fgColor: { rgb: "D9E1F2" } } };
            for (let r = 1; r <= 4; r++) {
                for (let c = 0; c < 10; c++) {
                    const ref = XLSX.utils.encode_cell({ r, c });
                    setStyle(ref, {
                        border: defaultBorder,
                        font: c % 5 === 0 ? { bold: true, sz: 14 } : { sz: 14 },
                        alignment: { vertical: "center", horizontal: c % 5 === 0 ? "left" : "center", wrapText: true },
                    });
                }
            }
            const headerTitleRow = 6;
            for (let c = 0; c < 10; c++) {
                const ref = XLSX.utils.encode_cell({ r: headerTitleRow, c });
                setStyle(ref, {
                    ...lightFill,
                    font: { bold: true, sz: 20 },
                    border: defaultBorder,
                    alignment: { horizontal: "center" },

                });
            }
            const headerRow = 7;
            for (let c = 0; c < 10; c++) {
                const ref = XLSX.utils.encode_cell({ r: headerRow, c });
                setStyle(ref, { ...lightFill, font: { bold: true, sz: 14 }, border: defaultBorder });
            }

            // 明細列 + 總計列
            for (let r = headerRow + 1; r <= totalRowIdx; r++) {
                for (let c = 0; c < 10; c++) {
                    const ref = XLSX.utils.encode_cell({ r, c });
                    setStyle(ref, {
                        font: { sz: 14 },
                        border: defaultBorder,
                        alignment: { vertical: "center", wrapText: true },

                    });
                }
            }
            // 總計列粗體
            setStyle(`A${totalRowIdx + 1}`, { font: { bold: true, sz: 20 }, alignment: { horizontal: "right" }, });
            setStyle(`G${totalRowIdx + 1}`, { font: { bold: true, sz: 20 }, alignment: { horizontal: "center" }, });
            setStyle(`I${totalRowIdx + 1}`, { font: { bold: true, sz: 20 } });

            /* ========== 5. 合併格 ========== */
            ws["!merges"] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: 9 } }, // 標題
                { s: { r: 1, c: 1 }, e: { r: 1, c: 4 } },
                { s: { r: 2, c: 1 }, e: { r: 2, c: 4 } },
                { s: { r: 3, c: 1 }, e: { r: 3, c: 9 } },
                { s: { r: 4, c: 1 }, e: { r: 4, c: 9 } },
                { s: { r: 1, c: 6 }, e: { r: 1, c: 9 } },
                { s: { r: 2, c: 6 }, e: { r: 2, c: 9 } },
                { s: { r: 5, c: 0 }, e: { r: 5, c: 9 } }, // 標題「報廢館藏列表」
                { s: { r: 6, c: 0 }, e: { r: 6, c: 9 } }, // 標題「報廢館藏列表」
                { s: { r: totalRowIdx, c: 0 }, e: { r: totalRowIdx, c: 5 } }, // 總計
                { s: { r: totalRowIdx, c: 6 }, e: { r: totalRowIdx, c: 7 } }, // 總計
                { s: { r: totalRowIdx, c: 8 }, e: { r: totalRowIdx, c: 9 } }, // 總計
            ];

            /* ========== 6. 欄寬 & 匯出 ========== */
            ws["!cols"] = [
                { wch: 12 },
                { wch: 14 },
                { wch: 26 },
                { wch: 14 },
                { wch: 22 },
                { wch: 14 },
                { wch: 12 },
                { wch: 12 },
                { wch: 18 },
                { wch: 16 },
            ];
            ws["!ref"] = `A1:J${totalRowIdx + 1}`;
            ws["!pageSetup"] = {
                orientation: "landscape",  // 橫向
                fitToWidth: 1,             // 一頁寬
                // fitToHeight: 10             // 高度不限
            };
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "報廢清單");

            const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
            saveAs(new Blob([out], { type: "application/octet-stream" }),
                `報廢清單_${d?.list_number}.xlsx`
            );
            setTimeout(() => {
                setLoading(false); // ✅ 在 download 完後再結束 loading
            }, 100);


        }
        catch (e) {
            message.error("匯出失敗，請稍後再試");
            setLoading(false)

        }
        finally {
            setLoading(false)
        }
    };


    const ScrappedListController = useCRUDController({
        apiRoute: "/library/scrapped_list",
        onFinish: () => {},
        onBefore: () => {}
    })


    return (
        <>
            <Button
                loading={propsLoading !== null ? propsLoading : loading}
                onClick={() => {
                    setLoading(true)
                    if (mode === "view") {
                        ScrappedListController.getData({
                            list_id: data?.list_id
                        }).then((res) => {
                            let data = res.data || []
                            let latestData = data.find(e => true)
                            handleExport(latestData, setLoading)
                        })
                    }
                    else handleExport(data, setLoading)
                }}
                className="text-white bg-green-600 hover:bg-green-700"
            >

                {(loading || propsLoading) ? "匯出中..." : "匯出 Excel"}
            </Button>
        </>

    );
};

export { ScrappedListExcelExport };
